import React from 'react';
import PropTypes from 'prop-types';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import DeliveryWindowModal from 'components/FrictionlessCheckout/SddSection/DeliveryWindow/DeliveryWindowModal';
import CheckoutApi from 'services/api/checkout';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import { sendSOTLinkTrackingEvent } from 'analytics/utils/sotTracking';
import { buildShippingSpeedSOTPayload } from 'analytics/utils/shippingSpeedTracking';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import DeliveryWindowOption from 'components/FrictionlessCheckout/SddSection/DeliveryWindow/DeliveryWindowOption';
import { Grid } from 'components/ui';

class DeliveryWindow extends BaseClass {
    constructor(props) {
        super(props);

        this.state = {};
    }

    componentDidMount() {
        const { selectedShippingMethodId } = this.props;
        const existingDefault = Storage.local.getItem(LOCAL_STORAGE.CHECKOUT_DEFAULT_SHIPPING_METHOD_ID);

        if (selectedShippingMethodId && !existingDefault) {
            Storage.local.setItem(LOCAL_STORAGE.CHECKOUT_DEFAULT_SHIPPING_METHOD_ID, selectedShippingMethodId, 7200000);
            this.fireShippingMethodDefaultAnalytics();
        }
    }

    componentDidUpdate(prevProps) {
        const { selectedShippingMethodId, scheduledSDDShippingMethodId, isDeliveryWindowScheduled } = this.props;

        if (selectedShippingMethodId === scheduledSDDShippingMethodId && !prevProps.isDeliveryWindowScheduled && isDeliveryWindowScheduled) {
            this.fireShippingMethodUpdateAnalytics(scheduledSDDShippingMethodId);
        }
    }

    fireShippingMethodDefaultAnalytics = () => {
        const shippingMethod = this.getSelectedShippingMethod();
        const shippingSpeedPayload = buildShippingSpeedSOTPayload({
            shippingMethod,
            defaultMethodId: this.props.selectedShippingMethodId
        });

        sendSOTLinkTrackingEvent({
            eventData: {
                linkName: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_DEFAULT,
                actionInfo: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_DEFAULT,
                specificEventName: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_DEFAULT,
                sotType: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_DEFAULT,
                ...shippingSpeedPayload
            }
        });
    };

    fireShippingMethodUpdateAnalytics = newMethodId => {
        const shippingMethod = this.findMethodById(newMethodId);
        const defaultMethodId = Storage.local.getItem(LOCAL_STORAGE.CHECKOUT_DEFAULT_SHIPPING_METHOD_ID);
        const shippingSpeedPayload = buildShippingSpeedSOTPayload({
            shippingMethod,
            defaultMethodId
        });

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                sTagInfo: {
                    ...shippingMethod,
                    actionInfo: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_UPDATE,
                    linkName: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_UPDATE
                },
                sotType: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_UPDATE,
                ...shippingSpeedPayload
            }
        });
    };

    getSelectedShippingMethod = () => {
        const { selectedShippingMethodId } = this.props;

        return this.findMethodById(selectedShippingMethodId);
    };

    findMethodById = methodId => {
        const { sameDayDeliveryMethodId, nddDeliveryMethodId, scheduledSDDShippingMethodId } = this.props;

        if (methodId === sameDayDeliveryMethodId) {
            return { shippingMethodType: 'Same Day Delivery', shippingMethodId: methodId };
        }

        if (methodId === nddDeliveryMethodId) {
            return { shippingMethodType: 'Next Day Delivery', shippingMethodId: methodId };
        }

        if (methodId === scheduledSDDShippingMethodId) {
            return {
                shippingMethodType: 'Schedule a Delivery Window',
                shippingMethodId: methodId,
                sameDayDeliveryLabel: this.props.deliveryWindowTitle
            };
        }

        return { shippingMethodId: methodId };
    };

    render() {
        const {
            sameDayDeliveryLabel,
            sameDayDeliveryMessage,
            sameDayDeliveryPrice,
            deliveryWindowTitle = '',
            deliveryWindowPrice,
            deliveryWindowMessage,
            displaySDUDeliveryPrice,
            isScheduledSDDDeliveryEnabled,
            sameDayShippingGroupId,
            scheduledSDDShippingMethodId,
            isDeliveryWindowScheduled,
            isFreeRougeSDD,
            nddShippingFee,
            nddDeliveryLabel,
            nddDeliveryMessage,
            isNddAvailable,
            selectedShippingMethodId,
            localization,
            sameDayDeliveryMethodId,
            nddDeliveryMethodId,
            isSddAvailable
        } = this.props;

        return (
            <>
                <Grid
                    gap={2}
                    marginTop={[0, 4]}
                    marginBottom={4}
                >
                    {isSddAvailable && (
                        <DeliveryWindowOption
                            label={sameDayDeliveryLabel}
                            message={sameDayDeliveryMessage}
                            price={sameDayDeliveryPrice}
                            isFreeRougeSDD={isFreeRougeSDD}
                            isActive={selectedShippingMethodId === sameDayDeliveryMethodId}
                            onClick={this.setSameDayDelivery}
                            isUserSDUMember={displaySDUDeliveryPrice}
                            localization={localization}
                        />
                    )}
                    {isNddAvailable && (
                        <DeliveryWindowOption
                            label={nddDeliveryLabel}
                            message={nddDeliveryMessage}
                            price={nddShippingFee}
                            isActive={selectedShippingMethodId === nddDeliveryMethodId}
                            isUserSDUMember={false}
                            localization={localization}
                            onClick={this.setNddDelivery}
                        />
                    )}
                    {isScheduledSDDDeliveryEnabled && (
                        <DeliveryWindowOption
                            label={deliveryWindowTitle}
                            message={deliveryWindowMessage}
                            messageColor={isDeliveryWindowScheduled ? 'blue' : ''}
                            price={deliveryWindowPrice}
                            onClick={isScheduledSDDDeliveryEnabled ? this.showScheduledDeliveryWindowModal : null}
                            isDisabled={!isScheduledSDDDeliveryEnabled}
                            isActive={isDeliveryWindowScheduled}
                            localization={localization}
                        />
                    )}
                </Grid>
                {this.state.showDeliveryWindowModal && (
                    <DeliveryWindowModal
                        onCloseCallback={this.onClose}
                        successCallback={this.onScheduleDeliveryWindow}
                        sameDaySchedules={this.state.sameDaySchedules}
                        deliveryWindowPrice={deliveryWindowPrice}
                        deliveryWindowTitle={deliveryWindowTitle}
                        sameDayShippingGroupId={sameDayShippingGroupId}
                        schedulingError={this.state.schedulingError}
                        scheduledSDDShippingMethodId={scheduledSDDShippingMethodId}
                        nddDeliveryMethodId={this.props.nddDeliveryMethodId}
                        sameDayDeliveryMethodId={this.props.sameDayDeliveryMethodId}
                        selectedShippingMethodId={this.props.selectedShippingMethodId}
                    />
                )}
            </>
        );
    }

    setSameDayDelivery = () => {
        if (!this.props.sameDayDeliveryMethodId) {
            return;
        }

        const shipMethodData = {
            orderId: 'current',
            shippingGroupId: this.props.sameDayShippingGroupId,
            shippingMethodId: this.props.sameDayDeliveryMethodId
        };

        this.props.setShippingMethod(
            shipMethodData,
            () => {
                this.fireAnalytics();
                this.fireShippingMethodUpdateAnalytics(this.props.sameDayDeliveryMethodId);
            },
            this.onScheduleDeliveryWindow,
            this.onClose
        );
    };

    setNddDelivery = () => {
        if (!this.props.nddDeliveryMethodId) {
            return;
        }

        const shipMethodData = {
            orderId: 'current',
            shippingGroupId: this.props.sameDayShippingGroupId,
            shippingMethodId: this.props.nddDeliveryMethodId
        };

        this.props.setShippingMethod(
            shipMethodData,
            () => {
                this.fireAnalytics();
                this.fireShippingMethodUpdateAnalytics(this.props.nddDeliveryMethodId);
            },
            this.onScheduleDeliveryWindow,
            this.onClose
        );
    };

    showScheduledDeliveryWindowModal = () => {
        CheckoutApi.getSamedaySchedules('current')
            .then(data => {
                this.fireAnalytics();
                this.setState({
                    showDeliveryWindowModal: true,
                    sameDaySchedules: data.schedules,
                    schedulingError: null
                });
            })
            .catch(e => {
                this.setState({
                    showDeliveryWindowModal: true,
                    schedulingError: e.errorMessages[0]
                });
            });
    };

    fireAnalytics = () => {
        const pageType = anaConsts.PAGE_NAMES.SAME_DAY_DELIVERY;
        const pageDetail = anaConsts.PAGE_TYPES.SAME_DAY_TIME_SELECTOR;
        const eventData = {
            pageName: `${pageType}:${pageDetail}:n/a:*`,
            pageType: pageType,
            pageDetail: pageDetail,
            linkData: anaConsts.LinkData.SAME_DAY_TIME_SELECTOR_OPEN
        };
        processEvent.process(anaConsts.ASYNC_PAGE_LOAD, { data: eventData });
    };

    onClose = e => {
        this.setState({ showDeliveryWindowModal: false });
        this.props.showInfoModal({
            isOpen: true,
            title: this.props.localization.errorTitle,
            message: e.errorMessages[0],
            buttonText: this.props.localization.ok,
            isHtml: true
        });
    };

    onScheduleDeliveryWindow = () => {
        this.setState({
            showDeliveryWindowModal: false
        });
        this.props.onDeliveryWindowSaved();
    };
}

DeliveryWindow.propTypes = {
    deliveryWindowTitle: PropTypes.string,
    sameDayDeliveryLabel: PropTypes.string.isRequired,
    sameDayDeliveryMessage: PropTypes.string.isRequired,
    sameDayDeliveryPrice: PropTypes.string.isRequired,
    deliveryWindowPrice: PropTypes.string,
    displaySDUDeliveryPrice: PropTypes.bool.isRequired,
    nddDeliveryLabel: PropTypes.string,
    nddDeliveryMessage: PropTypes.string,
    nddShippingFee: PropTypes.string,
    isNddAvailable: PropTypes.bool
};

export default wrapComponent(DeliveryWindow, 'DeliveryWindow');
